import { UzButton, UzTextarea } from '@utazzo/ui';
import { AlertTriangle, Check } from 'lucide-react';
import { useState } from 'react';

import { itemKindLabel } from '../item-kind';
import { useOpsAmendments, useResolveOpsAmendment } from '../use-ops-cases';

import type { OpsItemQueueRow } from '@utazzo/shared';

/**
 * On the lead page: items operations could not book and sent back to the
 * seller. Change it with the customer (edit the itinerary), then resolve - the
 * item goes back to operations to book. Renders nothing when there is none.
 */
const Row = ({ row }: { row: OpsItemQueueRow }): React.JSX.Element => {
  const resolve = useResolveOpsAmendment();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  return (
    <li className="rounded-lg border border-rose-200 bg-white px-3 py-2.5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 text-sm">
          <div className="font-medium text-zinc-900">
            {row.itemTitle}
            <span className="ml-1.5 text-xs font-normal text-zinc-500">
              {itemKindLabel(row.kind, row.vasKind)}
            </span>
          </div>
          <div className="text-xs text-rose-800">{row.amendmentReason}</div>
          <div className="text-[11px] text-zinc-500">
            From {row.opsOwnerName ?? 'operations'}
            {row.amendmentRequestedAt
              ? ` · ${row.amendmentRequestedAt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}`
              : ''}
          </div>
        </div>
        {!open ? (
          <UzButton size="sm" variant="outline" onClick={() => setOpen(true)}>
            <Check className="mr-1.5 h-3.5 w-3.5" /> Changed - resolve
          </UzButton>
        ) : null}
      </div>
      {open ? (
        <div className="mt-2 space-y-2">
          <UzTextarea
            rows={2}
            placeholder="What changed (e.g. moved to Sea Shell Havelock, customer agreed)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <UzButton size="sm" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </UzButton>
            <UzButton
              size="sm"
              loading={resolve.isPending}
              onClick={() =>
                resolve.mutate(
                  {
                    caseId: row.opsCaseId,
                    componentId: row.componentId,
                    input: note.trim() ? { note: note.trim() } : {},
                  },
                  { onSuccess: () => setOpen(false) },
                )
              }
            >
              Send back to operations
            </UzButton>
          </div>
        </div>
      ) : null}
    </li>
  );
};

export const LeadAmendmentsPanel = ({ leadId }: { leadId: string }): React.JSX.Element | null => {
  const query = useOpsAmendments(leadId);
  const rows = query.data ?? [];
  if (rows.length === 0) return null;
  return (
    <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50/60 p-3.5">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-rose-900">
        <AlertTriangle className="h-4 w-4" />
        Operations could not book {rows.length} item{rows.length === 1 ? '' : 's'} - change with the
        customer
      </div>
      <ul className="space-y-2">
        {rows.map((r) => (
          <Row key={`${r.opsCaseId}:${r.componentId}`} row={r} />
        ))}
      </ul>
    </div>
  );
};
